import { atom } from 'jotai';
import { atomWithReducer } from 'jotai/utils';

import { type AiGenerateDockRunPhase } from '../types/aiGenerateDock.types';
import { type AiBuilderQuizTypeHint } from '../utils/types';

/**
 * 생성기 실행 상태 — 전이는 reduceAiGenerateDockRun 한 곳이다. 실행 흐름은 useAiGenerateDockRun 이, 실패 자리
 * 걷기는 실패 카드가 액션으로 보낸다. 문제 목록 끝의 대기·실패 카드와 생성 중 토스트가 읽는다.
 * - generating: 실행 중. `applied` 는 문항이 목록에 들어오기 시작했다는 뜻(대기 카드를 걷는다).
 * - failed: 생성이 통째로 실패했다. `retry(count)` 는 같은 조건으로, 문항 수만 남은 실패 자리 수로 다시 실행한다.
 * 그 밖(성공·일부 성공·중단·거절)은 null 로 돌아간다.
 */
export type AiGenerateDockRunState =
  | {
      status: 'generating';
      /** 이 실행의 id — 이전 화면에서 늦게 끝난 실행이 새 실행 상태를 건드리지 않게 액션과 맞춰 본다. */
      runId: string;
      /** 자료 등록 중(preparing_source)인지 생성 중(generating)인지 — 알약 문구를 가른다. */
      phase: AiGenerateDockRunPhase;
      count: number;
      /** 첫 대기 카드의 문항 번호 — 기존 문제 수 + 1 */
      startNumber: number;
      /** 카드 헤더 유형 배지 — 요청한 유형 중 첫 번째 */
      quizType: AiBuilderQuizTypeHint;
      applied: boolean;
    }
  | {
      status: 'failed';
      count: number;
      quizType: AiBuilderQuizTypeHint;
      /** 남은 실패 자리 수 — 사용자가 자리를 지웠으면 그만큼만 다시 만든다. */
      retry: (count: number) => void;
    };

type AiGenerateDockFailedRun = Omit<
  Extract<AiGenerateDockRunState, { status: 'failed' }>,
  'status'
>;

export type AiGenerateDockRunAction =
  | {
      type: 'start';
      runId: string;
      phase: AiGenerateDockRunPhase;
      count: number;
      startNumber: number;
      quizType: AiBuilderQuizTypeHint;
    }
  | { type: 'phase'; runId: string; phase: AiGenerateDockRunPhase }
  | { type: 'applied'; runId: string }
  /** 실행 끝 — 통째 실패면 실패 자리, 그 밖은 비운다. */
  | { type: 'settle'; runId: string; failed: AiGenerateDockFailedRun | null }
  /** 실패 카드 한 장 걷기 — visibleCount 는 지금 목록 기준으로 보이는 실패 자리 수다. */
  | { type: 'dismissFailed'; visibleCount: number }
  /** 남은 칸이 없어 다시 만들 수 없는 실패 자리를 걷는다. */
  | { type: 'dropFailed' }
  | { type: 'reset' };

const isCurrentRun = (
  state: AiGenerateDockRunState | null,
  runId: string,
): state is Extract<AiGenerateDockRunState, { status: 'generating' }> =>
  state?.status === 'generating' && state.runId === runId;

export const reduceAiGenerateDockRun = (
  state: AiGenerateDockRunState | null,
  action: AiGenerateDockRunAction,
): AiGenerateDockRunState | null => {
  switch (action.type) {
    case 'start':
      return {
        status: 'generating',
        runId: action.runId,
        phase: action.phase,
        count: action.count,
        startNumber: action.startNumber,
        quizType: action.quizType,
        applied: false,
      };
    case 'phase':
      return isCurrentRun(state, action.runId)
        ? { ...state, phase: action.phase }
        : state;
    case 'applied':
      return isCurrentRun(state, action.runId)
        ? { ...state, applied: true }
        : state;
    case 'settle':
      // 그 실행이 지금 실행이 아니면(화면 이탈로 비웠거나 새 화면에서 새로 시작했으면) 늦게 온 끝은 무시한다.
      if (!isCurrentRun(state, action.runId)) return state;
      return action.failed ? { status: 'failed', ...action.failed } : null;
    case 'dismissFailed':
      if (state?.status !== 'failed') return state;
      return action.visibleCount > 1
        ? { ...state, count: action.visibleCount - 1 }
        : null;
    case 'dropFailed':
      return state?.status === 'failed' ? null : state;
    case 'reset':
      return null;
  }
};

export const aiGenerateDockRunAtom = atomWithReducer<
  AiGenerateDockRunState | null,
  AiGenerateDockRunAction
>(null, reduceAiGenerateDockRun);

/** 목록 끝 생성기 자리 — 문항이 들어오기 전의 대기 카드 또는 실패 카드. 없으면 null. */
export type AiGenerateDockSlot = AiGenerateDockRunState;

export const aiGenerateDockSlotAtom = atom<AiGenerateDockSlot | null>(get => {
  const run = get(aiGenerateDockRunAtom);
  if (!run || (run.status === 'generating' && run.applied)) return null;
  return run;
});

/** 생성 중인가 — 문항이 들어오기 시작해도 실행이 끝날 때까지는 생성 중이다. */
export const aiGenerateDockRunningCountAtom = atom<number | null>(get => {
  const run = get(aiGenerateDockRunAtom);
  return run?.status === 'generating' ? run.count : null;
});

/** 생성 중일 때의 단계 — 생성 중이 아니면 null. */
export const aiGenerateDockRunningPhaseAtom =
  atom<AiGenerateDockRunPhase | null>(get => {
    const run = get(aiGenerateDockRunAtom);
    return run?.status === 'generating' ? run.phase : null;
  });
